import React, { useState, useRef, useEffect } from 'react'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Button from '@cloudscape-design/components/button'
import Box from '@cloudscape-design/components/box'
import Input from '@cloudscape-design/components/input'
import type { BenchmarkRun, ChatMessage, ChatRequest } from '@/types'
import { useAppStore } from '@/store/app-store'
import { sendChatMessage } from '@/engine/api-client'
import { renderMarkdown } from './AnalysisPanel'
import {
  colorTextAccent,
  colorTextStatusSuccess,
} from '@cloudscape-design/design-tokens'

interface Props {
  run: BenchmarkRun | null
}

export function ChatPanel({ run }: Props) {
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatMessages = useAppStore(s => s.chatMessages)
  const chatLoading = useAppStore(s => s.chatLoading)
  const chatError = useAppStore(s => s.chatError)
  const addChatMessage = useAppStore(s => s.addChatMessage)
  const setChatLoading = useAppStore(s => s.setChatLoading)
  const setChatError = useAppStore(s => s.setChatError)
  const clearChat = useAppStore(s => s.clearChat)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  const handleSend = async () => {
    if (!run || !input.trim() || chatLoading) return
    const userMsg: ChatMessage = { role: 'user', content: input.trim() }
    addChatMessage(userMsg)
    setInput('')
    setChatLoading(true)
    setChatError(null)

    try {
      const allMessages = [...chatMessages, userMsg]
      const req: ChatRequest = {
        messages: allMessages,
        runId: run.id,
        benchmarkContext: {
          stats: run.stats,
          rawResults: run.results.map(r => ({
            ...r,
            chunkGaps: r.chunkGaps?.slice(0, 20) ?? [],
            prompt: r.prompt?.slice(0, 100) ?? '',
          })),
          profiles: run.profiles.map(p => {
            if ('imageAttachments' in p) {
              const { imageAttachments, ...rest } = p as any
              return { ...rest, imageCount: imageAttachments?.length || 0 }
            }
            return p
          }),
          networkProbes: run.networkProbes,
          prompt: run.results[0]?.prompt?.slice(0, 200) || '',
          modelNames: run.stats.map(s => s.profileName),
          runId: run.id,
          startTime: run.startTime,
          endTime: run.endTime,
          iterationsPerProfile: run.iterationsPerProfile,
        },
      }
      const reply = await sendChatMessage(req)
      addChatMessage({ role: 'assistant', content: reply })
    } catch (err: any) {
      setChatError(err.message || 'Failed to get response')
    } finally {
      setChatLoading(false)
    }
  }

  const suggestions = [
    'Compare chunk gap patterns between Bedrock and Anthropic',
    'Is there a pause between the first and second token?',
    'Which model has the best throughput?',
    'Analyze the guardrail latency overhead',
    'What causes the TTFT difference between models?',
    'Break down the network latency - where is time spent?',
    'How much overhead does the network add vs model inference?',
  ]

  return (
    <Container
      header={
        <Header
          actions={
            chatMessages.length > 0 ? (
              <Button variant="normal" onClick={clearChat}>Clear Chat</Button>
            ) : undefined
          }
        >
          💬 Benchmark Q&A
        </Header>
      }
    >
      <SpaceBetween size="m">
        {!run && (
          <Box textAlign="center" color="text-body-secondary" padding="l">
            Run a benchmark first, then ask questions about the results
          </Box>
        )}

        {run && chatMessages.length === 0 && (
          <SpaceBetween size="xs">
            <Box variant="small" color="text-body-secondary">Try asking:</Box>
            {suggestions.map((s, i) => (
              <Button key={i} variant="normal" onClick={() => setInput(s)}>
                <span style={{ textAlign: 'left', display: 'block', fontSize: 12 }}>{s}</span>
              </Button>
            ))}
          </SpaceBetween>
        )}

        {chatMessages.length > 0 && (
          <div style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 4 }}>
            <SpaceBetween size="s">
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    borderLeft: `3px solid ${msg.role === 'user' ? colorTextAccent : colorTextStatusSuccess}`,
                  }}
                >
                  <Box variant="small" color="text-body-secondary">
                    {msg.role === 'user' ? '🧑 You' : '🤖 AI'}
                  </Box>
                  <div style={{ fontSize: 13, lineHeight: 1.6, marginTop: 4 }}>
                    {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{ padding: '8px 12px', borderRadius: 8, borderLeft: `3px solid ${colorTextStatusSuccess}` }}>
                  <Box variant="small" color="text-body-secondary">🤖 AI</Box>
                  <Box color="text-status-info"><i>Analyzing benchmark data…</i></Box>
                </div>
              )}
              <div ref={messagesEndRef} />
            </SpaceBetween>
          </div>
        )}

        {chatError && (
          <Box color="text-status-error">{chatError}</Box>
        )}

        {run && (
          <SpaceBetween direction="horizontal" size="xs">
            <div style={{ flex: 1 }}>
              <Input
                value={input}
                onChange={({ detail }) => setInput(detail.value)}
                onKeyDown={(e) => {
                  if (e.detail.key === 'Enter') {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                placeholder="Ask about your benchmark results…"
                disabled={chatLoading}
              />
            </div>
            <Button
              variant="primary"
              disabled={chatLoading || !input.trim()}
              loading={chatLoading}
              onClick={handleSend}
            >
              Send
            </Button>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  )
}
